import React, { useMemo } from 'react';

import PageShell from '../ui-components/page/PageShell';
import PageHeader, {
  type BreadcrumbConfig,
} from '../ui-components/page/PageHeader';
import UnderConstruction from '../ui-components/page/UnderConstruction';
import {
  PageHeaderProvider,
  usePageHeaderContext,
} from '../context/PageHeaderContext';
import { usePageTitle } from '../hooks/usePageTitle';

import type { AppRouteConfig } from './ProtectedRoutes';

const resolveCurrentPath = (routePath: string): string | undefined => {
  if (routePath.includes(':')) {
    if (typeof globalThis !== 'undefined') {
      return globalThis.location.pathname || undefined;
    }
    return undefined;
  }
  return routePath;
};

const normalizeBreadcrumbs = (
  breadcrumbs: BreadcrumbConfig[],
  routePath: string
): BreadcrumbConfig[] => {
  return breadcrumbs.map((item, index) => {
    const isLast = index === breadcrumbs.length - 1;
    if (isLast) {
      const resolvedTo =
        item.to ??
        (item.href || item.onClick ? undefined : resolveCurrentPath(routePath));
      return {
        ...item,
        current: true,
        to: resolvedTo,
      };
    }
    const { to, href, onClick, ...rest } = item;
    return {
      ...rest,
      current: false,
    };
  });
};

const buildBreadcrumbs = (route: AppRouteConfig): BreadcrumbConfig[] => {
  if (route.breadcrumbs && route.breadcrumbs.length > 0) {
    return normalizeBreadcrumbs(route.breadcrumbs, route.path);
  }

  if (route.meta?.section) {
    return normalizeBreadcrumbs(
      [
        {
          label: route.meta.section,
          to: route.meta.sectionPath,
        },
        { label: route.title },
      ],
      route.path
    );
  }

  return normalizeBreadcrumbs([{ label: route.title }], route.path);
};

const PageShellWithHeader: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { header } = usePageHeaderContext();
  return (
    <PageShell
      header={
        <PageHeader
          title={header.title}
          breadcrumbs={header.breadcrumbs}
          actions={header.actions}
          subtitle={header.subtitle}
        />
      }
    >
      {children}
    </PageShell>
  );
};

const RouteShell: React.FC<{ route: AppRouteConfig }> = ({ route }) => {
  const breadcrumbs = useMemo(() => buildBreadcrumbs(route), [route]);
  const RouteComponent = route.element;
  const isUnderConstruction =
    Boolean(route.meta?.underConstruction) ||
    Boolean(
      (route.element as { isUnderConstruction?: boolean })?.isUnderConstruction
    );

  usePageTitle(route.title);

  if (isUnderConstruction) {
    return <UnderConstruction title={route.title} breadcrumbs={breadcrumbs} />;
  }

  return (
    <PageHeaderProvider title={route.title} breadcrumbs={breadcrumbs}>
      <PageShellWithHeader>
        <RouteComponent />
      </PageShellWithHeader>
    </PageHeaderProvider>
  );
};

export default RouteShell;
